import { DateTime, Interval } from "luxon"

export function formatISODate(
  dateTime,
  format = "MM/dd/yyyy hh:mm a",
  { zone } = {}
) {
  if (!dateTime) return null
  const dt = zone
    ? DateTime.fromISO(dateTime, { zone })
    : DateTime.fromISO(dateTime)
  return dt.toFormat(format)
}

// Matches a specific-quarter preset like "Q3 2026" — generated dynamically
// from the current date rather than hardcoded, so it ages out on its own.
const QUARTER_LABEL_PATTERN = /^Q([1-4]) (\d{4})$/

export function getDateRange(rangeString) {
  const now = DateTime.now()
  let start, end

  const quarterMatch = QUARTER_LABEL_PATTERN.exec(rangeString)
  if (quarterMatch) {
    const [, quarter, year] = quarterMatch
    const quarterStartMonth = (Number(quarter) - 1) * 3 + 1
    start = DateTime.fromObject({
      year: Number(year),
      month: quarterStartMonth,
      day: 1,
    }).startOf("month")
    return Interval.fromDateTimes(
      start,
      start.plus({ months: 2 }).endOf("month")
    )
  }

  switch (rangeString) {
    case "Today":
      start = now.startOf("day")
      end = now.endOf("day")
      break
    case "Yesterday":
      start = now.minus({ days: 1 }).startOf("day")
      end = now.minus({ days: 1 }).endOf("day")
      break
    case "This Week":
      start = now.startOf("week")
      end = now.endOf("week")
      break
    case "Last Week":
      start = now.minus({ weeks: 1 }).startOf("week")
      end = now.minus({ weeks: 1 }).endOf("week")
      break
    case "This Month":
      start = now.startOf("month")
      end = now.endOf("month")
      break
    case "Last Month":
      start = now.minus({ months: 1 }).startOf("month")
      end = now.minus({ months: 1 }).endOf("month")
      break
    case "This Year":
      start = now.startOf("year")
      end = now.endOf("year")
      break
    default:
      throw new Error("Invalid range string")
  }

  return Interval.fromDateTimes(start, end)
}

/*
  Display grouping for a list of already-loaded records — this is NOT a filter.
  `getDateRange` above is the filter, and it deliberately stays as it is: it
  throws on anything outside its supported range strings, and "Older" has no
  server-side counterpart to hand it.

  The buckets are residual. They are tested in order and the first match wins,
  so THIS_WEEK means "this week, excluding today and yesterday". On a Monday
  that puts Sunday under YESTERDAY rather than LAST_WEEK, and on the 1st of a
  month it puts yesterday under YESTERDAY rather than LAST_MONTH — which is how
  anyone reading the list expects it to behave.

  Weeks are luxon's ISO weeks (Monday start), matching `getDateRange`, so a
  record can never sit under "Last week" while the "This Week" filter returns it.
*/
export const dateGroups = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  LAST_WEEK: "LAST_WEEK",
  THIS_MONTH: "THIS_MONTH",
  LAST_MONTH: "LAST_MONTH",
  THIS_YEAR: "THIS_YEAR",
  LAST_YEAR: "LAST_YEAR",
  OLDER: "OLDER",
})

// CreatedDate is ISO in every payload seen so far, but the accordion this
// replaced read it with `new Date()`, which is laxer. Stay at least as lax so a
// format change shows up as a wrong bucket rather than a blank list.
const parseDate = (value) => {
  if (!value) return null
  const fromISO = DateTime.fromISO(value)
  if (fromISO.isValid) return fromISO
  const fromJS = DateTime.fromJSDate(new Date(value))
  return fromJS.isValid ? fromJS : null
}

export function getDateGroup(isoDate, now = DateTime.now()) {
  const dt = parseDate(isoDate)
  if (!dt) return dateGroups.OLDER

  // A CreatedDate ahead of the clock (server skew) would otherwise fall all the
  // way through to OLDER, which is the worst answer available.
  if (dt > now.endOf("day")) return dateGroups.TODAY

  if (dt.hasSame(now, "day")) return dateGroups.TODAY
  if (dt.hasSame(now.minus({ days: 1 }), "day")) return dateGroups.YESTERDAY
  if (dt.hasSame(now, "week")) return dateGroups.THIS_WEEK
  if (dt.hasSame(now.minus({ weeks: 1 }), "week")) return dateGroups.LAST_WEEK
  if (dt.hasSame(now, "month")) return dateGroups.THIS_MONTH
  if (dt.hasSame(now.minus({ months: 1 }), "month"))
    return dateGroups.LAST_MONTH
  if (dt.hasSame(now, "year")) return dateGroups.THIS_YEAR
  if (dt.hasSame(now.minus({ years: 1 }), "year")) return dateGroups.LAST_YEAR

  return dateGroups.OLDER
}

// "Aug 12 – 18, 2026" when the week sits inside one month, "Jul 29 – Aug 4,
// 2026" when it crosses one, and the full "MMM d, yyyy" on both ends on the
// rare week that crosses a year boundary — the year only needs stating once
// unless leaving it off the start would be ambiguous.
const formatWeekRange = (start, end) => {
  if (start.hasSame(end, "month") && start.hasSame(end, "year")) {
    return `${start.toFormat("MMM d")} – ${end.toFormat("d, yyyy")}`
  }
  if (start.hasSame(end, "year")) {
    return `${start.toFormat("MMM d")} – ${end.toFormat("MMM d, yyyy")}`
  }
  return `${start.toFormat("MMM d, yyyy")} – ${end.toFormat("MMM d, yyyy")}`
}

/*
  Today and Yesterday name their date. Every other bucket spans a range, and
  names it too — "This week"/"Last week" get their Monday-to-Sunday dates,
  "This month"/"Last month" their month name, "This year"/"Last year" their
  year. OLDER is unbounded on one end, so it stays as plain text.

  The week/month/year ranges describe the whole calendar unit, not just the
  days that can actually land in a residual bucket (see getDateGroup) — "This
  week" always means Monday through Sunday of the current week, even though
  today and yesterday's records show up under their own dividers instead.
*/
export function formatDateGroupLabel(group, isoDate, now = DateTime.now()) {
  if (group === dateGroups.TODAY || group === dateGroups.YESTERDAY) {
    const prefix = group === dateGroups.TODAY ? "Today" : "Yesterday"
    const date = formatISODate(isoDate, "MMM d, yyyy")
    return date ? `${prefix} · ${date}` : prefix
  }

  if (group === dateGroups.THIS_WEEK || group === dateGroups.LAST_WEEK) {
    const isThisWeek = group === dateGroups.THIS_WEEK
    const reference = isThisWeek ? now : now.minus({ weeks: 1 })
    const range = formatWeekRange(
      reference.startOf("week"),
      reference.endOf("week")
    )
    return `${isThisWeek ? "This week" : "Last week"} · ${range}`
  }

  if (group === dateGroups.THIS_MONTH || group === dateGroups.LAST_MONTH) {
    const isThisMonth = group === dateGroups.THIS_MONTH
    const reference = isThisMonth ? now : now.minus({ months: 1 })
    return `${isThisMonth ? "This month" : "Last month"} · ${reference.toFormat("MMMM yyyy")}`
  }

  if (group === dateGroups.THIS_YEAR || group === dateGroups.LAST_YEAR) {
    const isThisYear = group === dateGroups.THIS_YEAR
    const reference = isThisYear ? now : now.minus({ years: 1 })
    return `${isThisYear ? "This year" : "Last year"} · ${reference.toFormat("yyyy")}`
  }

  return "Older than last year"
}

/*
  Buckets a CreatedDate:DESC list into [{ group, label, rows }].

  `now` is computed once for the whole list: a per-row DateTime.now() can
  straddle midnight and emit two "Today" dividers. Nesting rows inside their
  group (rather than interleaving dividers into one flat array) is what lets a
  sticky divider be pushed out by the next one instead of stacking on it.
*/
export function groupByDate(rows, getDate) {
  const now = DateTime.now()
  const groups = []

  for (const row of rows) {
    const isoDate = getDate(row)
    const group = getDateGroup(isoDate, now)
    const last = groups[groups.length - 1]

    if (last?.group === group) {
      last.rows.push(row)
    } else {
      groups.push({
        group,
        label: formatDateGroupLabel(group, isoDate, now),
        rows: [row],
      })
    }
  }

  return groups
}
